import * as THREE from 'three';
import { PhysicsWorld } from '../core/PhysicsWorld';
import { AIVehicle, ObstacleVehicle } from './AIVehicle';
import { AIPedestrian } from './AIPedestrian';
import { AICyclist } from './AICyclist';
import { AIBus } from './AIBus';
import { TrafficLight } from '../environment/TrafficLights';

export class TrafficManager {
  public vehicles: AIVehicle[] = [];
  public pedestrians: AIPedestrian[] = [];
  public cyclists: AICyclist[] = [];
  public bus: AIBus | null = null;
  public highwayVehicles: AIVehicle[] = [];
  public trafficLights: TrafficLight[] = [];
  public group: THREE.Group;

  constructor(private scene: THREE.Scene, private physicsWorld?: PhysicsWorld) {
    this.group = new THREE.Group();
    this.scene.add(this.group);
    this.spawnInitialTraffic();
  }

  private spawnInitialTraffic() {
    // ── Road Layout Reference ────────────────────────────────────────────────
    // Main N-S boulevard: x ≈ 0.  Right lane northbound: x ≈ -2.  Southbound: x ≈ +2
    // Main E-W avenue:    z ≈ 0.  Right lane eastbound:  z ≈ -2.  Westbound:  z ≈ +2
    // Roundabout centre: (0,0,0), radius ≈ 10–12 m.  Cars circle at r = 14.5.
    // Residential 30-zone cross-street at z = 60 and x = ±50.
    // Player starts at (-2.0, 0.5, 24) heading North.
    // ────────────────────────────────────────────────────────────────────────

    // 1. Roundabout circling car — starts on SOUTH side far from the player's entry point
    // Circulates COUNTER-CLOCKWISE per StVO Zeichen 215 (-r*sin(a), r*cos(a))
    const circleWps: THREE.Vector3[] = [];
    const r = 14.5;
    const NUM_ROUNDABOUT_WPS = 16;
    for (let i = 0; i < NUM_ROUNDABOUT_WPS; i++) {
      const a = ((i + 8) / NUM_ROUNDABOUT_WPS) * Math.PI * 2;
      circleWps.push(new THREE.Vector3(-r * Math.sin(a), 0.3, r * Math.cos(a)));
    }
    const aiRoundaboutCar = new AIVehicle(0x2563eb, circleWps, this.physicsWorld);
    aiRoundaboutCar.speedKmh = 22;
    this.vehicles.push(aiRoundaboutCar);
    this.group.add(aiRoundaboutCar.mesh);

    // 2. Southbound car on main boulevard in the CORRECT lane (x ≈ +2.2, heading -Z)
    // Starts at z=110 (≥20m from player at z=24) in closed 2-lane circuit
    const mainSouthboundWps = [
      new THREE.Vector3(2.2, 0.3, 110),
      new THREE.Vector3(2.2, 0.3, 70),
      new THREE.Vector3(2.2, 0.3, 30),
      new THREE.Vector3(2.2, 0.3, 20),
      new THREE.Vector3(1.0, 0.3, 18.5),
      new THREE.Vector3(-1.0, 0.3, 18.5),
      new THREE.Vector3(-2.2, 0.3, 22),
      new THREE.Vector3(-2.2, 0.3, 30),
      new THREE.Vector3(-2.2, 0.3, 70),
      new THREE.Vector3(-2.2, 0.3, 110),
      new THREE.Vector3(-1.0, 0.3, 114),
      new THREE.Vector3(1.0, 0.3, 114),
    ];
    const aiSouthCar = new AIVehicle(0x16a34a, mainSouthboundWps, this.physicsWorld);
    aiSouthCar.speedKmh = 40;
    this.vehicles.push(aiSouthCar);
    this.group.add(aiSouthCar.mesh);

    // 3. Northbound car on main boulevard in correct lane (x ≈ -2.2, heading +Z)
    // Starts at z=70 ahead of player (≥20m from player at z=24) in closed 2-lane circuit
    const mainNorthboundWps = [
      new THREE.Vector3(-2.2, 0.3, 70),
      new THREE.Vector3(-2.2, 0.3, 110),
      new THREE.Vector3(-1.0, 0.3, 114),
      new THREE.Vector3(1.0, 0.3, 114),
      new THREE.Vector3(2.2, 0.3, 110),
      new THREE.Vector3(2.2, 0.3, 70),
      new THREE.Vector3(2.2, 0.3, 30),
      new THREE.Vector3(2.2, 0.3, 20),
      new THREE.Vector3(1.0, 0.3, 18.5),
      new THREE.Vector3(-1.0, 0.3, 18.5),
      new THREE.Vector3(-2.2, 0.3, 22),
      new THREE.Vector3(-2.2, 0.3, 50),
    ];
    const aiNorthCar = new AIVehicle(0xdc2626, mainNorthboundWps, this.physicsWorld);
    aiNorthCar.speedKmh = 38;
    this.vehicles.push(aiNorthCar);
    this.group.add(aiNorthCar.mesh);

    // 4. Rechts-vor-Links training car coming from RIGHT at z=60 cross-street (2-lane loop)
    const rvlWps = [
      new THREE.Vector3(80, 0.3, 61.5),
      new THREE.Vector3(50, 0.3, 61.5),
      new THREE.Vector3(10, 0.3, 61.5),
      new THREE.Vector3(-10, 0.3, 61.5),
      new THREE.Vector3(-50, 0.3, 61.5),
      new THREE.Vector3(-80, 0.3, 61.5),
      new THREE.Vector3(-84, 0.3, 60),
      new THREE.Vector3(-80, 0.3, 58.5),
      new THREE.Vector3(-50, 0.3, 58.5),
      new THREE.Vector3(-10, 0.3, 58.5),
      new THREE.Vector3(10, 0.3, 58.5),
      new THREE.Vector3(50, 0.3, 58.5),
      new THREE.Vector3(80, 0.3, 58.5),
      new THREE.Vector3(84, 0.3, 60),
    ];
    const aiRightCar = new AIVehicle(0xd97706, rvlWps, this.physicsWorld);
    aiRightCar.speedKmh = 25;
    this.vehicles.push(aiRightCar);
    this.group.add(aiRightCar.mesh);

    // 5. German Transit Bus (Linienbus 102) serving the bus stop at z = 15
    // Starts at z=110 (≥20m from player at z=24), with index 2 as bus stop bay
    const busWps = [
      new THREE.Vector3(2.2, 0.4, 110),
      new THREE.Vector3(3.2, 0.4, 35),
      new THREE.Vector3(3.4, 0.4, 15), // bus stop bay
      new THREE.Vector3(2.2, 0.4, -10), // merges back into southbound lane
      new THREE.Vector3(2.2, 0.4, -60),
      new THREE.Vector3(2.2, 0.4, -110),
      new THREE.Vector3(1.0, 0.4, -114),
      new THREE.Vector3(-1.0, 0.4, -114),
      new THREE.Vector3(-2.2, 0.4, -110),
      new THREE.Vector3(-2.2, 0.4, -60),
      new THREE.Vector3(-2.2, 0.4, -10),
      new THREE.Vector3(-2.2, 0.4, 20),
      new THREE.Vector3(-2.2, 0.4, 70),
      new THREE.Vector3(-2.2, 0.4, 110),
      new THREE.Vector3(-1.0, 0.4, 114),
      new THREE.Vector3(1.0, 0.4, 114),
    ];
    this.bus = new AIBus(busWps, this.physicsWorld);
    this.group.add(this.bus.mesh);

    // 6. AI Cyclist on Roundabout Red Cycle Track (r = 19.2m)
    // Starts on SOUTH side of roundabout (z = -19.2, ≥20m from player at z=24)
    // Circulates COUNTER-CLOCKWISE per StVO (-rBike*sin(a), rBike*cos(a))
    const bikeWps: THREE.Vector3[] = [];
    const rBike = 19.2;
    for (let i = 0; i < 16; i++) {
      const a = ((i + 8) / 16) * Math.PI * 2;
      bikeWps.push(new THREE.Vector3(-rBike * Math.sin(a), 0.1, rBike * Math.cos(a)));
    }
    const aiCyclist = new AICyclist('bicycle', 0xdc2626, bikeWps, 16, this.physicsWorld);
    this.cyclists.push(aiCyclist);
    this.group.add(aiCyclist.mesh);

    // 7. AI Electric Scooter rider in 30-Zone / pedestrian crossing
    const scooterWps = [
      new THREE.Vector3(20, 0.1, 58.5),
      new THREE.Vector3(45, 0.1, 58.5),
      new THREE.Vector3(45, 0.1, 20),
      new THREE.Vector3(20, 0.1, 20),
    ];
    const aiScooter = new AICyclist('scooter', 0x10b981, scooterWps, 15, this.physicsWorld);
    this.cyclists.push(aiScooter);
    this.group.add(aiScooter.mesh);

    // 8. Pedestrians on Zebrastreifen (StVO Zeichen 293)
    const pedZebra = new AIPedestrian(
      new THREE.Vector3(4.8, 0, 78),
      new THREE.Vector3(-4.8, 0, 78),
      this.physicsWorld
    );
    this.pedestrians.push(pedZebra);
    this.group.add(pedZebra.mesh);

    const pedZebra2 = new AIPedestrian(
      new THREE.Vector3(-5.0, 0, 42),
      new THREE.Vector3(5.0, 0, 42),
      this.physicsWorld
    );
    this.pedestrians.push(pedZebra2);
    this.group.add(pedZebra2.mesh);

    // Residential Zebrastreifen at (30, 0, 60)
    const pedZebra3 = new AIPedestrian(
      new THREE.Vector3(30, 0, 64.5),
      new THREE.Vector3(30, 0, 55.5),
      this.physicsWorld
    );
    this.pedestrians.push(pedZebra3);
    this.group.add(pedZebra3.mesh);

    // Residential Zebrastreifen at (-30, 0, 60)
    const pedZebra4 = new AIPedestrian(
      new THREE.Vector3(-30, 0, 55.5),
      new THREE.Vector3(-30, 0, 64.5),
      this.physicsWorld
    );
    this.pedestrians.push(pedZebra4);
    this.group.add(pedZebra4.mesh);

    // Sidewalk pedestrian strolling past Altstadt shops
    const pedShop = new AIPedestrian(
      new THREE.Vector3(5.5, 0, 20),
      new THREE.Vector3(5.5, 0, 52),
      this.physicsWorld
    );
    this.pedestrians.push(pedShop);
    this.group.add(pedShop.mesh);

    // ── NEW ENTITIES (Task 5 - World Expansion) ──────────────────────────────

    // 9. AUTOBAHN LEFT LANE — Fast Overtaker #1 (130 km/h, clockwise loop)
    const abLeftWps1 = [
      new THREE.Vector3(0, 0.4, -574.75),
      new THREE.Vector3(560, 0.4, -574.75),
      new THREE.Vector3(574.75, 0.4, 0),
      new THREE.Vector3(560, 0.4, 574.75),
      new THREE.Vector3(0, 0.4, 574.75),
      new THREE.Vector3(-560, 0.4, 574.75),
      new THREE.Vector3(-574.75, 0.4, 0),
      new THREE.Vector3(-560, 0.4, -574.75),
    ];
    const abFast1 = new AIVehicle(0xe74c3c, abLeftWps1, this.physicsWorld);
    abFast1.speedKmh = 130;
    this.highwayVehicles.push(abFast1);
    this.vehicles.push(abFast1);
    this.group.add(abFast1.mesh);

    // 10. AUTOBAHN LEFT LANE — Fast Overtaker #2 (125 km/h, starts offset half-way)
    const abLeftWps2 = [
      new THREE.Vector3(560, 0.4, 574.75),
      new THREE.Vector3(0, 0.4, 574.75),
      new THREE.Vector3(-560, 0.4, 574.75),
      new THREE.Vector3(-574.75, 0.4, 0),
      new THREE.Vector3(-560, 0.4, -574.75),
      new THREE.Vector3(0, 0.4, -574.75),
      new THREE.Vector3(560, 0.4, -574.75),
      new THREE.Vector3(574.75, 0.4, 0),
    ];
    const abFast2 = new AIVehicle(0xc0392b, abLeftWps2, this.physicsWorld);
    abFast2.speedKmh = 125;
    this.highwayVehicles.push(abFast2);
    this.vehicles.push(abFast2);
    this.group.add(abFast2.mesh);

    // 11. AUTOBAHN RIGHT LANE — Cruising Delivery Van (95 km/h, clockwise, right lane = inner carriageway)
    const abRightWps1 = [
      new THREE.Vector3(0, 0.5, -585.25),
      new THREE.Vector3(560, 0.5, -585.25),
      new THREE.Vector3(585.25, 0.5, 0),
      new THREE.Vector3(560, 0.5, 585.25),
      new THREE.Vector3(0, 0.5, 585.25),
      new THREE.Vector3(-560, 0.5, 585.25),
      new THREE.Vector3(-585.25, 0.5, 0),
      new THREE.Vector3(-560, 0.5, -585.25),
    ];
    const abVan = new AIVehicle(0xf0f0f0, abRightWps1, this.physicsWorld);
    abVan.speedKmh = 95;
    this.highwayVehicles.push(abVan);
    this.vehicles.push(abVan);
    this.group.add(abVan.mesh);

    // 12. LANDSTRASSE NORTH — Rural car shuttling N Landstraße (90 km/h)
    const landNorthWps = [
      new THREE.Vector3(-2, 0.3, 200),
      new THREE.Vector3(-2, 0.3, 350),
      new THREE.Vector3(-2, 0.3, 520),
      new THREE.Vector3(2, 0.3, 520),
      new THREE.Vector3(2, 0.3, 350),
      new THREE.Vector3(2, 0.3, 200),
    ];
    const landCar1 = new AIVehicle(0x3b82f6, landNorthWps, this.physicsWorld);
    landCar1.speedKmh = 90;
    this.vehicles.push(landCar1);
    this.group.add(landCar1.mesh);

    // 13. LANDSTRASSE SOUTH — Rural car shuttling S Landstraße (88 km/h)
    const landSouthWps = [
      new THREE.Vector3(2, 0.3, -200),
      new THREE.Vector3(2, 0.3, -350),
      new THREE.Vector3(2, 0.3, -520),
      new THREE.Vector3(-2, 0.3, -520),
      new THREE.Vector3(-2, 0.3, -350),
      new THREE.Vector3(-2, 0.3, -200),
    ];
    const landCar2 = new AIVehicle(0x10b981, landSouthWps, this.physicsWorld);
    landCar2.speedKmh = 88;
    this.vehicles.push(landCar2);
    this.group.add(landCar2.mesh);

    // 14. RESIDENTIAL BOTTLENECK — Oncoming car approaching Engstelle Street 1 (z=60)
    const engCar1Wps = [
      new THREE.Vector3(-10, 0.3, 61.5),
      new THREE.Vector3(-35, 0.3, 61.5),
      new THREE.Vector3(-55, 0.3, 61.5),
      new THREE.Vector3(-80, 0.3, 61.5),
      new THREE.Vector3(-55, 0.3, 58.5),
      new THREE.Vector3(-35, 0.3, 58.5),
      new THREE.Vector3(-10, 0.3, 58.5),
    ];
    const engCar1 = new AIVehicle(0xfbbf24, engCar1Wps, this.physicsWorld);
    engCar1.speedKmh = 25;
    this.vehicles.push(engCar1);
    this.group.add(engCar1.mesh);

    // 15. BICYCLE on Fahrradstraße West (-116 to -26, safe distance from boulevard)
    const bikeWps2 = [
      new THREE.Vector3(-116, 0.1, 30),
      new THREE.Vector3(-80, 0.1, 30),
      new THREE.Vector3(-55, 0.1, 30),
      new THREE.Vector3(-26, 0.1, 30),
      new THREE.Vector3(-55, 0.1, 30),
      new THREE.Vector3(-80, 0.1, 30),
    ];
    const fahrradCyclist = new AICyclist('bicycle', 0x16a34a, bikeWps2, 18, this.physicsWorld);
    this.cyclists.push(fahrradCyclist);
    this.group.add(fahrradCyclist.mesh);

    // 16. SECOND BICYCLE on Fahrradstraße East (26 to 116, safe distance from boulevard)
    const bikeWps3 = [
      new THREE.Vector3(116, 0.1, 30),
      new THREE.Vector3(80, 0.1, 30),
      new THREE.Vector3(55, 0.1, 30),
      new THREE.Vector3(26, 0.1, 30),
      new THREE.Vector3(55, 0.1, 30),
      new THREE.Vector3(80, 0.1, 30),
    ];
    const fahrradCyclist2 = new AICyclist('bicycle', 0x22c55e, bikeWps3, 16, this.physicsWorld);
    this.cyclists.push(fahrradCyclist2);
    this.group.add(fahrradCyclist2.mesh);

    // 17. E-SCOOTER — hugs outer residential ring (curb-side)
    const scooterWps2 = [
      new THREE.Vector3(116, 0.1, 58.5),
      new THREE.Vector3(116, 0.1, -58.5),
      new THREE.Vector3(50, 0.1, -116),
      new THREE.Vector3(-50, 0.1, -116),
      new THREE.Vector3(-116, 0.1, -58.5),
      new THREE.Vector3(-116, 0.1, 58.5),
      new THREE.Vector3(-50, 0.1, 116),
      new THREE.Vector3(50, 0.1, 116),
    ];
    const eScooter2 = new AICyclist('scooter', 0x06b6d4, scooterWps2, 22, this.physicsWorld);
    this.cyclists.push(eScooter2);
    this.group.add(eScooter2.mesh);
  }

  public readonly START_SPAWN_POS = new THREE.Vector3(-2.0, 0.5, 24);
  public hasDeparted: boolean = false;

  public setTrafficLights(lights: TrafficLight[]) {
    this.trafficLights = lights;
  }

  public isSpawnBubbleActive(playerPos?: THREE.Vector3, playerSpeedKmh: number = 0): boolean {
    if (this.hasDeparted) return false;
    if (!playerPos) return false;
    const distFromSpawn = playerPos.distanceTo(this.START_SPAWN_POS);
    return distFromSpawn < 8.0 && playerSpeedKmh < 3.0;
  }

  // Player position and telemetry are passed each frame to enable proximity-based collision avoidance and spawn bubble defense
  public update(
    deltaTime: number,
    playerPos?: THREE.Vector3,
    playerSpeedKmh: number = 0,
    elapsedTime: number = 0
  ) {
    // Departure detection: once student drives away (> 5 km/h or > 8m from start spawn), departure is permanently latched
    if (playerPos && !this.hasDeparted) {
      if (playerPos.distanceTo(this.START_SPAWN_POS) > 8.0 || playerSpeedKmh > 5.0) {
        this.hasDeparted = true;
      }
    }

    const bubbleActive = playerPos && this.isSpawnBubbleActive(playerPos, playerSpeedKmh);

    // ── 12m Spawn Bubble Protection Guard (Initial Spawn Area Only) ───────────
    if (bubbleActive && playerPos) {
      const BUBBLE_RADIUS = 12.0;

      // Guard vehicles
      for (const v of this.vehicles) {
        // Never freeze vehicles ahead driving away from spawn (heading North on boulevard)
        if (v.position.z - playerPos.z > 1.0) {
          continue;
        }

        const d = v.position.distanceTo(playerPos);
        if (d < BUBBLE_RADIUS) {
          if (d < 8.0) {
            // Overlapping or uncomfortably close inside spawn bubble: warp to next waypoint >= 15m away
            for (let i = 0; i < v.waypoints.length; i++) {
              const nextIdx = (v.currentWaypointIdx + i + 1) % v.waypoints.length;
              if (v.waypoints[nextIdx].distanceTo(playerPos) >= 15.0) {
                v.currentWaypointIdx = nextIdx;
                v.position.copy(v.waypoints[nextIdx]);
                v.mesh.position.copy(v.position);
                if (v.body) {
                  v.body.position.set(v.position.x, v.position.y, v.position.z);
                  v.body.velocity.set(0, 0, 0);
                }
                break;
              }
            }
          }
          // Hold at perimeter
          v.speedKmh = 0;
        }
      }

      // Guard bus
      if (this.bus) {
        const dB = this.bus.position.distanceTo(playerPos);
        if (dB < BUBBLE_RADIUS) {
          if (dB < 10.0) {
            for (let i = 0; i < this.bus.waypoints.length; i++) {
              const nextIdx = (this.bus.currentWaypointIdx + i + 1) % this.bus.waypoints.length;
              if (this.bus.waypoints[nextIdx].distanceTo(playerPos) >= 18.0) {
                this.bus.currentWaypointIdx = nextIdx;
                this.bus.position.copy(this.bus.waypoints[nextIdx]);
                this.bus.mesh.position.copy(this.bus.position);
                if (this.bus.body) {
                  this.bus.body.position.set(this.bus.position.x, this.bus.position.y, this.bus.position.z);
                  this.bus.body.velocity.set(0, 0, 0);
                }
                break;
              }
            }
          }
          this.bus.speedKmh = 0;
        }
      }

      // Guard cyclists
      for (const c of this.cyclists) {
        const dC = c.position.distanceTo(playerPos);
        if (dC < BUBBLE_RADIUS) {
          if (dC < 6.5) {
            for (let i = 0; i < c.waypoints.length; i++) {
              const nextIdx = (c.currentWaypointIdx + i + 1) % c.waypoints.length;
              if (c.waypoints[nextIdx].distanceTo(playerPos) >= 15.0) {
                c.currentWaypointIdx = nextIdx;
                c.position.copy(c.waypoints[nextIdx]);
                c.mesh.position.copy(c.position);
                if (c.body) {
                  c.body.position.set(c.position.x, c.position.y, c.position.z);
                  c.body.velocity.set(0, 0, 0);
                }
                break;
              }
            }
          }
          c.speedKmh = 0;
        }
      }
    }

    // ── Build Vehicle Obstacles Array for Multi-Agent Queueing & Pedestrian Awareness
    const allObstacleVehicles: ObstacleVehicle[] = [];
    for (const v of this.vehicles) {
      allObstacleVehicles.push({
        position: v.position,
        speedKmh: v.speedKmh,
        halfLength: 2.2,
        entity: v,
      });
    }
    if (this.bus) {
      allObstacleVehicles.push({
        position: this.bus.position,
        speedKmh: this.bus.speedKmh,
        halfLength: 4.5,
        entity: this.bus,
      });
    }

    // ── Entity Updates with Full Player Telemetry, Traffic Lights, Pedestrians & Multi-Agent Queueing
    for (const v of this.vehicles) {
      v.update(deltaTime, playerPos, playerSpeedKmh, this.trafficLights, this.pedestrians, allObstacleVehicles);
    }
    for (const p of this.pedestrians) {
      p.update(deltaTime, playerPos, allObstacleVehicles);
    }
    for (const c of this.cyclists) {
      c.update(deltaTime, playerPos, playerSpeedKmh, this.trafficLights, this.pedestrians, allObstacleVehicles);
    }
    if (this.bus) {
      this.bus.update(deltaTime, playerPos, playerSpeedKmh, this.trafficLights, this.pedestrians, allObstacleVehicles);
    }
  }
}
